import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Button } from 'react-bootstrap';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { FORM_MODE, Permissions } from '@awp/constants';
import { FormInput, FormTextArea, FormSwitch } from '@awp/components/Common/AwpFormInputs';
import Authorize from '@awp/components/Authorize';
import AwpCloseCancelButtons from '@awp/components/AwpCloseCancelButton';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import PatternImageTool from './PatternImageTool';
import { getCrud } from '@awp/shared/Crud';

const SoilTypesEdit = ({ mode, data, setMode, setData, onAdd, onUpdate }) => {
  const defaultDto = {
    Id: 0,
    Code: '',
    Description: '',
    PatternImage: null,
    IsActive: true,
    ConcurrencyControlNumber: 0,
  };
  const [soilTypeData, setSoilTypeData] = useState(mode !== FORM_MODE.CREATE ? { ...data } : defaultDto);
  const [entityErrors, setEntityErrors] = useState(null);
  const [pendingPattern, setPendingPattern] = useState(null);
  const [isImageRemoved, setIsImageRemoved] = useState(false); // Track if image is removed in edit mode

  useEffect(() => {
    if (mode !== FORM_MODE.CREATE && data) {
      setSoilTypeData({ ...data, PatternImage: data.PatternImage });
      setIsImageRemoved(false); // Reset when loading new data
    }
  }, [mode, data]);

  const crud = getCrud('Soil Type', mode);
  const { title, buttonLabel, readOnly } = crud;

  const breadcrumbs = () => setMode(FORM_MODE.LIST);

  const handleSubmit = async (values, { setSubmitting, resetForm, setErrors }) => {
    setSubmitting(true);
    const formData = {
      id: soilTypeData?.Id,
      code: values.code,
      description: values.description,
      patternImage: values.patternImage ?? pendingPattern,
      isActive: values.isActive ? 'Y' : 'N',
      concurrencyControlNumber: soilTypeData?.ConcurrencyControlNumber,
    };
    soilTypeData.PatternImage = values.patternImage ?? pendingPattern;
    try {
      const result = mode === FORM_MODE.EDIT ? await onUpdate(formData) : await onAdd(formData);
      setSoilTypeData(result);
      resetForm({ values: { code: result.code, description: result.description, patternImage: result.patternImage } });
      setData(result);
      setMode(FORM_MODE.VIEW);
      setIsImageRemoved(false); // Reset after successful save
      setPendingPattern(null); // Clear pending pattern after save
    } catch (err) {
      setErrors({ entity: [err.message['Soil Type']] });
      setEntityErrors({ ['Soil Type']: [err.message['Soil Type']] });
    } finally {
      setSubmitting(false);
    }
  };

  // Callback from PatternImageTool when image is removed
  const handleImageRemoved = () => {
    setIsImageRemoved(true);
    setPendingPattern(null); // Clear pending pattern when image is removed
  };

  const disableScaleTool = mode === FORM_MODE.EDIT && !isImageRemoved && !!soilTypeData?.PatternImage;

  const isFormPristine = (initialValues, values) => {
    const fieldsEqual =
      initialValues.code === values.code &&
      initialValues.description === values.description &&
      initialValues.isActive === values.isActive;

    return (
      fieldsEqual &&
      ((!pendingPattern && !soilTypeData?.PatternImage) || (!isImageRemoved && !!soilTypeData?.PatternImage))
    );
  };

  const initialValues = {
    code: soilTypeData?.Code || '',
    description: soilTypeData?.Description || '',
    patternImage: soilTypeData?.PatternImage || null,
    isActive: soilTypeData?.IsActive === 'Y',
  };

  return (
    <>
      <Formik
        enableReinitialize
        validateOnMount={true}
        initialValues={initialValues}
        validationSchema={Yup.object({
          code: Yup.string()
            .required('Soil Type Code is required')
            .matches(/^[A-Z0-9/]+$/, 'Only uppercase letters, digits, and "/" are allowed')
            .max(8, 'Maximum 8 characters allowed')
            .transform((value) => (value || '').toUpperCase()),
          description: Yup.string().max(4000, 'Maximum 4000 characters allowed').required('Description is required'),
          patternImage: Yup.mixed().test(
            'fileFormat',
            'Unsupported Format. Only JPG, PNG, GIF, or SVG allowed (max 1MB).',
            (value) => !value || typeof value === 'string',
          ),
        })}
        onSubmit={handleSubmit}
      >
        {(props) => (
          <Form>
            <span className="awp-text-btn" onClick={breadcrumbs}>
              List Soil Types
            </span>
            <span className="mx-3">{'>'}</span>
            <span>{title}</span>
            <Card border="light" className="page-card mt-3">
              <Card.Header>
                <Row className="mt-4"></Row>
                {crud.getHeader()}
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col sm={6} className="mb-4">
                    <FormInput
                      label="Soil Type Code"
                      name="code"
                      id="code"
                      readOnly={mode !== FORM_MODE.CREATE}
                      onChange={(e) => props.setFieldValue('code', e.target.value.toUpperCase())}
                    />
                  </Col>
                  {mode !== FORM_MODE.CREATE && (
                    <Col sm={6} className="mb-4">
                      <FormSwitch
                        useMargin={true}
                        readOnly={readOnly}
                        id="isActive"
                        name="isActive"
                        label="Active"
                        value={soilTypeData?.IsActive === 'Y'}
                      />
                    </Col>
                  )}
                </Row>
                <Row>
                  <Col sm={12} md={6} lg={6} className="mb-4">
                    <FormTextArea
                      label="Description"
                      name="description"
                      id="description"
                      rows={4}
                      readOnly={readOnly}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12} className="mb-4">
                    <PatternImageTool
                      onPatterApproved={(base64) => setPendingPattern(base64)}
                      onPatternChange={(base64) => props.setFieldValue('patternImage', base64)}
                      initialPattern={soilTypeData?.PatternImage}
                      onRemove={handleImageRemoved} // New callback for image removal
                      readOnly={readOnly}
                      disableScaleTool={disableScaleTool}
                    />
                  </Col>
                </Row>
              </Card.Body>
              <Card.Footer>
                {!readOnly ? (
                  <AwpCloseCancelButtons
                    props={props}
                    isPristine={isFormPristine(initialValues, props.values) || props.isSubmitting}
                    hasAccess={true}
                    onConfirmedCancel={() => setMode(FORM_MODE.LIST)}
                    submitButtonLabel={buttonLabel}
                  />
                ) : (
                  <div className="d-flex">
                    <Button variant="outline-primary" onClick={() => setMode(FORM_MODE.LIST)}>
                      Close
                    </Button>
                    <Authorize requires={[Permissions.WriteSoilType]}>
                      <Button className="ml-2" variant="outline-primary" onClick={() => setMode(FORM_MODE.EDIT)}>
                        Edit Soil Type
                      </Button>
                    </Authorize>
                  </div>
                )}
              </Card.Footer>
            </Card>
          </Form>
        )}
      </Formik>
      <AwpValidationErrorModal setErrors={setEntityErrors} errors={entityErrors} />
    </>
  );
};

export default SoilTypesEdit;
